import { videoFavoriteState, videoLikedState } from 'constant/video'
import { useCallback, useEffect, useRef, useState, useContext, useMemo, createContext } from 'react'
import CoverImage from '../CoverImage'
import InfoWrapper from '../InfoWrapper'
import Toolbar from '../Toolbar'
import {
  StyledCoverImage,
  StyledHomeUploaderInfo,
  StyledShortPlayer,
  StyledTouchEffect,
  StyledUploaderInfo,
  StyledVirtualPage,
} from './Styled'
import PromotionInfo from '../PromotionInfo'
import { useSelector } from 'react-redux'
import { selectTopicList } from 'redux/selector/app'
import { fetchPostAdEvent, SchemaOholoMaterialShow, SchemaShortVideoShow } from 'api'
import { formatDate } from 'utils/timeFormat'
import ToolbarHome from '../ToolbarHome'

// ---- Player control registry (Context) ----
// 讓祖先層級可用 key 直接呼叫 player 控制器
interface PlayerControlContextValue {
  register: (key: any, api: any) => () => void
  get: (key: any) => any
  remove: (key: any) => void
}

// PlayerPage 註冊到 registry 的 API 形狀（供 usePlayerControl 消費）
interface RegisteredPlayerApi {
  pause: () => void
  play: () => void
  currentTime: (second: number) => void
  setAutoplay: (bool: boolean) => void
  isPaused: () => boolean
}

const PlayerControlContext = createContext<PlayerControlContextValue | null>(null)

interface PlayerControlProviderProps {
  children: React.ReactNode
}

export function PlayerControlProvider({ children }: PlayerControlProviderProps) {
  const storeRef = useRef(new Map())

  const register = useCallback((key: any, api: any) => {
    storeRef.current.set(key, api)
    return () => storeRef.current.delete(key)
  }, [])

  const get = useCallback((key: any) => {
    return storeRef.current.get(key)
  }, [])

  const remove = useCallback((key: any) => {
    storeRef.current.delete(key)
  }, [])

  const value = useMemo(() => ({ register, get, remove }), [register, get, remove])

  return <PlayerControlContext.Provider value={value}>{children}</PlayerControlContext.Provider>
}

export function usePlayerControl(key: any) {
  const ctx = useContext(PlayerControlContext)

  const pause = useCallback(() => {
    ctx?.get(key)?.pause()
  }, [ctx, key])

  const play = useCallback(() => {
    ctx?.get(key)?.play()
  }, [ctx, key])

  const currentTime = useCallback(
    (second: number) => {
      ctx?.get(key)?.currentTime(second)
    },
    [ctx, key]
  )

  const remove = useCallback(
    (key: any) => {
      ctx?.remove(key)
    },
    [ctx]
  )

  const setAutoplay = useCallback(
    (bool: boolean) => {
      ctx?.get(key)?.setAutoplay(bool)
    },
    [ctx, key]
  )

  // key-bound、無參數（與 pause/play 同慣例）。
  // 找不到該 item（尚未註冊 / 已卸載）時回傳 true：與 RegisteredPlayerApi.isPaused
  // 的安全預設一致，避免呼叫端誤判為「原本在播」而觸發多餘的恢復播放
  const isPaused = useCallback((): boolean => {
    return ctx?.get(key)?.isPaused() ?? true
  }, [ctx, key])

  return { pause, play, currentTime, remove, setAutoplay, isPaused }
}
// ---- /Player control registry (Context) ----

interface PlayerPageProps {
  mode?: 'video' | 'promotion'
  videoInfo?: any
  onBase64ImageLoad?: any
  posterSrc?: string | null
  onLike?: () => void
  onSave?: () => void
  onShare?: (video: SchemaShortVideoShow) => void
  promoteInfo?: SchemaOholoMaterialShow
  autoplay?: boolean
  preloadedImages?: any[]
  // default, home
  toolbarStyle?: 'default' | 'home'
}

const PlayerPage = ({
  mode = 'video',
  videoInfo: videoInfoProp,
  onBase64ImageLoad,
  posterSrc = null,
  onLike,
  onSave,
  onShare,
  promoteInfo,
  autoplay = false,
  preloadedImages = [],
  // default, home
  toolbarStyle = 'default',
}: PlayerPageProps) => {
  const playerControlCtx = useContext(PlayerControlContext)
  const playerRef = useRef<any>()

  const [videoInfo, setVideoInfo] = useState<any>(videoInfoProp)

  // 註冊到 Context：祖先層級可以用 usePlayerControl(controlKey) 直接控制
  useEffect(() => {
    if (mode !== 'video') return
    if (!playerControlCtx) return
    if (videoInfo?.id == null) return

    const unregister = playerControlCtx.register(videoInfo?.id, {
      pause: () => {
        playerRef.current?.pause()
      },
      play: () => {
        playerRef.current?.play()
      },
      currentTime: (second: number) => {
        playerRef.current?.currentTime(second)
      },
      setAutoplay: (bool: boolean) => {
        playerRef.current?.setAutoplay(bool)
      },
      isPaused: (): boolean => {
        // playerRef 尚未掛載（元件正在卸載/尚未初始化）時視為「已暫停」的安全預設，
        // 理由同 ShortPlayer.isPaused 的預設值：避免呼叫端誤判為「原本在播」而做多餘的恢復播放
        return playerRef.current?.isPaused() ?? true
      },
    } satisfies RegisteredPlayerApi)

    return unregister
  }, [playerControlCtx, videoInfo?.id, mode])

  if (videoInfoProp != null && videoInfoProp !== videoInfo) {
    setVideoInfo(videoInfoProp)
  }

  const onDoubleCLick = (ev: MouseEvent) => {
    const spawnEffect = touchEffectRef.current?.spawnEffect
    const container: HTMLDivElement | undefined = touchEffectRef.current?.el
    if (container && spawnEffect) {
      const basePosition = container.getBoundingClientRect()
      const { x, y } = basePosition
      const startRotation = Math.PI * (Math.random() * 2 - 1) * 0.1
      const waveRotation = Math.PI * (Math.random() * 2 - 1) * 0.2
      const offsetX = (Math.random() - 0.5) * 50
      const offsetY = (Math.random() - 0.5) * 50
      const startScale = 0
      const endScale = 1.1 + Math.random() * 0.1
      spawnEffect(
        ev.clientX - x + offsetX,
        ev.clientY - y + offsetY,
        startRotation,
        startRotation + waveRotation,
        startScale,
        endScale
      )
    }

    if (videoInfo.like !== videoLikedState.TRUE) {
      onLike?.()
    }
  }
  const touchEffectRef = useRef<any>()

  /**
   * on promotion page click
   */
  const onClick = useCallback(() => {
    if (promoteInfo) {
      fetchPostAdEvent({
        date: formatDate(new Date()),
        material_id: promoteInfo.id,
        material_name: promoteInfo.name,
        oholo_site_id: promoteInfo.site_type,
        oholoer_id: promoteInfo.oholoer_id,
        oholoer_link: promoteInfo.oholoer_link,
        oholoer_name: promoteInfo.oholoer_name,
      })
      window.open(promoteInfo?.link, '_blank')
    }
  }, [promoteInfo])

  const topicList = useSelector(selectTopicList)

  let topics: any[] = []

  if (mode === 'video' && videoInfo) {
    topics =
      videoInfo.topic_ids
        ?.map((id: any) => {
          const topicItem = topicList.find((t: any) => t.id === id)
          if (topicItem) {
            return {
              text: '#' + topicItem.name,
              id: topicItem.id,
              params: {
                topic_ids: String(topicItem.id),
              },
            }
          } else {
            return null
          }
        })
        .filter((text: any) => text != null) ?? []
  }

  const coverImageItemMain =
    videoInfoProp == null
      ? []
      : posterSrc
      ? [
          <StyledCoverImage
            // imageStyle={{ objectFit: 'contain' }}
            style={{ background: 'black' }}
            key={'loaded-' + videoInfoProp.coverphoto_v}
            src={posterSrc}
            onBase64ImageLoad={onBase64ImageLoad}
            base64SrcSource={videoInfoProp.coverphoto_v}
            objectFit={toolbarStyle === 'default' ? 'contain' : 'auto'}
          />,
        ]
      : [
          <StyledCoverImage
            // imageStyle={{ objectFit: 'contain' }}
            style={{ background: 'black' }}
            key={videoInfo?.coverphoto_v}
            onBase64ImageLoad={onBase64ImageLoad}
            base64SrcSource={videoInfo?.coverphoto_v}
            objectFit={toolbarStyle === 'default' ? 'contain' : 'auto'}
          />,
        ]

  const currentCoverKey = videoInfoProp?.coverphoto_v ?? videoInfo?.coverphoto_v
  const safePreloadedImages = Array.isArray(preloadedImages) ? preloadedImages : []
  const preloadedImagesFiltered = safePreloadedImages.filter((i) => i.key === currentCoverKey)

  const coverArray = [
    ...(preloadedImagesFiltered && preloadedImagesFiltered.length > 0
      ? preloadedImagesFiltered.map((i: any) => (
          <StyledCoverImage
            key={'loaded-' + i.key}
            src={i.value}
            base64SrcSource={i.key}
            // imageStyle={{ objectFit: 'contain' }}
            style={{ background: 'black' }}
            objectFit={toolbarStyle === 'default' ? 'contain' : 'auto'}
          />
        ))
      : coverImageItemMain),
  ]

  return (
    <StyledVirtualPage>
      {videoInfo && (
        <InfoWrapper style={mode === 'video' ? {} : { display: 'none' }}>
          {coverArray}
          <StyledShortPlayer
            ref={playerRef}
            videoInfo={videoInfo}
            poster={posterSrc}
            onDoubleCLick={onDoubleCLick}
            autoplay={autoplay}
            objectFit={toolbarStyle === 'default' ? 'contain' : 'auto'}
          />
          {toolbarStyle === 'default' ? (
            <StyledUploaderInfo
              topics={topics}
              uploader={'@' + videoInfo.uploader_name}
              uploaderId={videoInfo.uploader_id}
              videoName={videoInfo.title}
            />
          ) : (
            <StyledHomeUploaderInfo
              topics={topics}
              uploader={'@' + videoInfo.uploader_name}
              uploaderId={videoInfo.uploader_id}
              videoName={videoInfo.title}
            />
          )}
          <StyledTouchEffect ref={touchEffectRef} />
        </InfoWrapper>
      )}
      {mode === 'video' &&
        videoInfo &&
        (toolbarStyle === 'default' ? (
          <Toolbar
            key={videoInfo.id}
            liked={videoInfo.like === videoLikedState.TRUE}
            saved={videoInfo.favorite === videoFavoriteState.TRUE}
            likeCount={videoInfo.like_count}
            saveCount={videoInfo.favorite_count}
            onLike={onLike}
            onSave={onSave}
          ></Toolbar>
        ) : (
          <ToolbarHome
            key={videoInfo.id}
            liked={videoInfo.like === videoLikedState.TRUE}
            saved={videoInfo.favorite === videoFavoriteState.TRUE}
            likeCount={videoInfo.like_count}
            saveCount={videoInfo.favorite_count}
            onLike={onLike}
            onSave={onSave}
            onShare={() => onShare?.(videoInfo)}
            uploaderId={videoInfo.uploader_id}
            uploaderIcon={videoInfo.uploader_icon}
          ></ToolbarHome>
        ))}
      {mode !== 'video' && promoteInfo && (
        <InfoWrapper onClick={onClick}>
          <CoverImage
            src={posterSrc}
            onBase64ImageLoad={onBase64ImageLoad}
            base64SrcSource={promoteInfo.icon_path}
            source={'picture'}
            style={{ pointerEvents: 'none' }}
            objectFit={toolbarStyle === 'default' ? 'contain' : 'auto'}
          />
          <PromotionInfo promoter="@官方推荐" description={promoteInfo?.name} link={promoteInfo?.link} />
        </InfoWrapper>
      )}
    </StyledVirtualPage>
  )
}

export default PlayerPage
